import PortfolioCardItem from "containers/portfolio/ViewAllPortfolioPropertiesModal/components/PortfolioCardItem/PropertyCardItem";
import {
  AccountPhoneNumberStatusesEnum,
  BrokerLicenseStatusesEnum,
} from "dash-common";
import { ModalContext, PaginationLoadMore, Spinner } from "dash-ui";
import {
  GetCurrentBrokerQuery,
  PortfoliosByPublicFolderListItemFragment,
} from "gql/generated.graphql";
import { MutableRefObject, useContext } from "react";
import { FiX } from "react-icons/fi";
import { MdVerified } from "react-icons/md";

// Types copied from old component until refactor
type BrokerModalProps = {
  broker: GetCurrentBrokerQuery["getCurrentBroker"];
  properties: Array<PortfoliosByPublicFolderListItemFragment>;
  propertiesTotal: number;
  isLoading: boolean;
  loadMore?: {
    fetchMoreProps?: {
      fetchMore: (props: any) => Promise<any>;
      variables: any;
      queryName: string;
    };
  };
  contactFormRef: MutableRefObject<HTMLInputElement | null>;
};

export function BrokerModal(props: BrokerModalProps) {
  // Hooks
  const useModal = useContext(ModalContext);
  // Derived states
  const isVerifiedBroker =
    props.broker.account.phone?.status ===
      AccountPhoneNumberStatusesEnum.VERIFIED &&
    props.broker.license?.licenseStatus === BrokerLicenseStatusesEnum.ACTIVE;
  // Handle contact broker for large screens
  function contactBrokerLargeScreens() {
    props.contactFormRef.current?.focus();
    useModal.toggleModal(false, "ViewAllPortfolioPropertiesModal");
  }
  // Handle contact broker for small screens
  function contactBrokerSmallScreens() {
    useModal.toggleModal(true, "BrokerInfoModal");
    useModal.toggleModal(false, "ViewAllPortfolioPropertiesModal");
  }
  // Handle check availability
  function checkAvailability() {
    if (!props.broker) return;
    useModal.toggleModal(true, "BrokerAvailabilityModal");
  }
  // Handle close this modal
  function closeBrokerModal() {
    useModal.toggleModal(false, "ViewAllPortfolioPropertiesModal");
  }
  // Return nothing if modal is closed
  if (!useModal.isOpenCurrent["ViewAllPortfolioPropertiesModal"]) {
    return <></>;
  }
  // Return HTML component
  return (
    <div
      className="fixed top-0 left-0 flex flex-col items-center w-full h-screen rounded-lg border-2 border-white max-w-screen bg-black bg-opacity-30 px-4 py-20 z-10 overflow-auto"
      onClick={closeBrokerModal}>
      <div
        className="flex flex-col bg-white rounded-lg w-full max-w-screen-lg"
        onClick={(event) => event.stopPropagation()}>
        {/* Header - Medium and large screens */}
        <header className="hidden md:flex relative h-96 bg-blue-900 rounded-t-lg overflow-hidden">
          <div className="absolute flex w-4/5 h-full rounded-br-full bg-black bg-opacity-20" />
          <div className="absolute flex w-full h-full items-center">
            {props.broker.profile?.avatarFile?.publicUrl && (
              <img
                src={props.broker.profile?.avatarFile?.publicUrl}
                alt={props.broker.profile.firstName}
                className="w-72 rounded-xl ml-8"
              />
            )}
            <div className="flex flex-col ml-8">
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <h1 className="text-white font-bold text-3xl font-[montserrat]">
                    {props.broker.profile.firstName}{" "}
                    {props.broker.profile.lastName}
                  </h1>
                  {props.broker.agencyName && <h2 className="text-white font-semibold">{props.broker.agencyName}</h2>}
                </div>
                {isVerifiedBroker && (
                  <MdVerified className="text-green-400 size-5" />
                )}
              </div>
              <p className="text-sky-100 mt-2">{props.broker.description}</p>
              <div className="flex flex-col lg:flex-row gap-4 mt-8">
                <button
                  className="h-10 px-4 rounded bg-sky-50 text-sky-700 w-48"
                  onClick={contactBrokerLargeScreens}>
                  Contact Me
                </button>
                <button
                  className="h-10 px-4 rounded bg-sky-50 text-sky-700 w-48"
                  onClick={checkAvailability}>
                  Check Availability
                </button>
              </div>
            </div>
          </div>
          <button className="size-8 absolute top-4 right-4 hover:bg-white hover:bg-opacity-15 flex items-center justify-center rounded">
            <FiX className="size-6 text-white" onClick={closeBrokerModal} />
          </button>
        </header>
        {/* Header - Small screens */}
        <header className="flex md:hidden flex-col bg-blue-900 relative">
          <img
            src={props.broker.profile?.avatarFile?.publicUrl}
            alt={props.broker.profile.firstName}
            className="h-64 object-cover"
          />
          <div className="p-8">
            <div className="flex gap-4 items-center">
              <div>
                <h1 className="text-white font-bold text-2xl font-[montserrat]">
                  {props.broker.profile.firstName} {props.broker.profile.lastName}
                </h1>
                {props.broker.agencyName && <h2 className="text-white font-semibold">{props.broker.agencyName}</h2>}
              </div>
              {isVerifiedBroker && <MdVerified className="text-green-400 size-5" />}
            </div>
            <p className="text-sky-100 mt-1">{props.broker.description}</p>
            <div className="flex flex-col gap-4 mt-8">
              <button
                className="h-10 px-4 rounded bg-sky-50 text-sky-700"
                onClick={contactBrokerSmallScreens}>
                Contact Me
              </button>
              <button
                className="h-10 px-4 rounded bg-sky-50 text-sky-700"
                onClick={checkAvailability}>
                Check Availability
              </button>
            </div>
          </div>
          <button className="size-8 absolute top-2 right-2 bg-blue-800 flex items-center justify-center rounded">
            <FiX className="size-6 text-white" onClick={closeBrokerModal} />
          </button>
        </header>
        <div className="flex flex-col p-8">
          {props.isLoading && (
            <div className="flex justify-center flex-row">
              <Spinner />
              <h1 className="ml-4 font-bold text-xl">Loading properties...</h1>
            </div>
          )}
          {!props.isLoading && (
            <>
              <p className="font-bold text-xl">
                Property Portfolio ({props.propertiesTotal})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 mb-8">
                {props.properties.map(function (property) {
                  return (
                    <PortfolioCardItem
                      portfolioItem={property}
                      broker={props.broker}
                      key={property._id}
                    />
                  );
                })}
              </div>
              <PaginationLoadMore
                fetchMoreProps={props.loadMore?.fetchMoreProps}>
                Load More
              </PaginationLoadMore>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
